import React, { useEffect, useCallback, useState, useRef } from 'react';

import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Button, Row, Col, Container } from 'react-bootstrap';
import { addMinutes, parseISO, format, isBefore } from 'date-fns';

import { useToast } from '../../../hooks/toast';

import api from '../../../services/api';

import SelectSync, { IOption } from '../../../components/SelectSync';
import Input from '../../../components/Input';
import Loading from '../../../components/Loading';

import {
  CONFIRMACAO_AGENDA,
  optionsHorasAtendimento,
  STATUS_AGENDA,
} from '../../../utils/funcoes';
import getValidationErrors from '../../../utils/getValidationErrors';
import getResponseErrors from '../../../utils/getResponseErrors';
import { IAgendamento } from '../../../components/Agendamento';
import { useAuth } from '../../../hooks/auth';
import { PermissaoAdmin } from '../../../components/Agendamento/styles';
import { ProcedimentoCheckup } from '..';
import { Container as ContainerCheck } from '../../PacienteAtendimento/PedidoExames/CheckboxContainer/styles';
import Checkbox from '../../../components/Exame/CheckBox';

interface IFormData {
  AgeData: Date;
  AgeHora: string;
  AgeConfCodigo: string;
  login?: string;
  senha?: string;
}

export interface IReturnAgendamentoTratamento {
  AgeCodigo: number;
  AgeInicio: Date;
  AgeInicioFormatado: string;
  AgeConfCodigo: number;
  updated_at: Date;
  valor: number;
}

interface IDadosAgendamento {
  ConCodigo: number;
  AgeClinica: number;
}

interface ICheck {
  name: string;
  key: string;
  label: string;
  checked?: boolean;
}

interface IAgendamentoAdicionado extends IAgendamento {
  AgeCodigo: number;
}

type ComponentProps = {
  checkupId: number;
  pacCodigo: number;
  perfil: number;
  procedimentos: ProcedimentoCheckup[];
  handleFechar(dados: IReturnAgendamentoTratamento): void;
};

const CheckupAgendamento: React.FC<ComponentProps> = ({
  checkupId,
  pacCodigo,
  perfil,
  procedimentos,
  handleFechar,
}) => {
  const formRef = useRef<FormHandles>(null);
  const { clinica } = useAuth();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);

  const [optionsAgendas, setOptionsAgendas] = useState<IOption[] | undefined>(
    undefined,
  );
  const [exibePermissaoAdmin, setExibePermissaoAdmin] = useState(false);
  const [mensagemPermissaoAdmin, setMensagemPermissaoAdmin] = useState('');

  const [optionsHora, _] = useState(() => optionsHorasAtendimento());
  const initialData = { AgeData: format(new Date(), 'yyyy-MM-dd') };

  const [checkedItems, setCheckedItems] = useState<string>('');
  const [itens, setItens] = useState<ICheck[]>([]);
  const checkRef = useRef(null);

  useEffect(() => {
    const procedimentosFormatado = procedimentos.map(proc => {
      return {
        name: `${proc.ProCodigo}-${proc.ProNome}`,
        label: `${proc.ProNome}`,
        key: `${proc.ProCodigo}`,
        checked: true,
      };
    });

    setItens(procedimentosFormatado);

    const its = procedimentosFormatado
      .filter(item => {
        return item.checked;
      })
      .map(i => i.key)
      .join(',');

    setCheckedItems(its);
  }, [procedimentos]);
  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (perfil) {
          // selecionar agendas do perfil

          const responseAgenda = await api.get<IOption[]>(
            `/agendas/porperfil/transferir?ConPerCodigo=${perfil}`,
          );

          setOptionsAgendas(responseAgenda.data);
        }
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar disponibilidade',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (checkupId) loadDados();
  }, [clinica.CliCodigo, perfil, checkupId, addToast]);

  const handleSubmit = useCallback(
    async (dados: IFormData) => {
      setLoading(true);
      try {
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          AgeData: Yup.date().required(),
          AgeHora: Yup.string().required('Hora obrigatório'),
          AgeConfCodigo: Yup.string().required('Agenda obrigatória'),
          ...(exibePermissaoAdmin
            ? {
                login: Yup.string().required('Login é obrigatório'),
                senha: Yup.string().required('Senha é obrigatória'),
              }
            : {}),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataAgendamento = parseISO(
          `${dados.AgeData} ${dados.AgeHora}:00`,
        );

        const agendamento = {
          AgeConfCodigo: dados.AgeConfCodigo,
          AgeInicio: dataAgendamento,
          AgeFim: addMinutes(dataAgendamento, procedimentos[0].ProDuracao),
          AgePacCodigo: pacCodigo,
          AgeFormaPag: 9,
          AgeClinica: clinica.CliCodigo,
          AgeObs: '',
          AgeStatus: STATUS_AGENDA.AGENDADO,
          AgeConfirmado: CONFIRMACAO_AGENDA.AGUARDANDO_CONFIRMACAO,
          AgePlano: false,
          ...(exibePermissaoAdmin
            ? { permissao: { UsuLogin: dados.login, UsuSenha: dados.senha } }
            : {}),
          duracao: procedimentos[0].ProDuracao,
          procedimentos: itens
            .filter(it => it.checked)
            .map(pro => ({
              ProCodigo: Number(pro.key),
              ProNome: pro.label,
              ProValor: 0,
            })),
        };

        const agendamentoAdicionado = await api.post<IAgendamentoAdicionado>(
          `/agendamentos/checkup/${checkupId}`,
          agendamento,
        );
        // pode pegar o retorno e jogar para a agenda

        addToast({ title: 'Agendamento salvo', type: 'success' });

        handleFechar({
          AgeCodigo: agendamentoAdicionado.data.AgeCodigo,
          AgeInicio: agendamentoAdicionado.data.AgeInicio,
          AgeInicioFormatado: format(
            parseISO(agendamentoAdicionado.data.AgeInicio.toString()),
            'dd/MM/yyyy HH:mm',
          ),
          AgeConfCodigo: agendamentoAdicionado.data.AgeConfCodigo,
          updated_at: agendamentoAdicionado.data.updated_at,
          valor: 0,
        });
      } catch (error: any) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        const statusErro = error?.response?.status;
        if (statusErro === 403) {
          setExibePermissaoAdmin(true);
          setMensagemPermissaoAdmin(mensagemErro);
        }
        addToast({
          type: 'error',
          title: 'Houve um erro ao salvar agendamento',
          description: mensagemErro,
          timeAlive: 10000,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      checkupId,
      pacCodigo,
      procedimentos,
      itens,
      clinica.CliCodigo,
      handleFechar,
    ],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  const handleChange = useCallback(
    e => {
      const nomeProcedimento = e.target.name;
      const isChecked = e.target.checked;

      const newItens = itens.map(it => {
        if (it.name === nomeProcedimento) return { ...it, checked: isChecked };
        return it;
      });

      const its = newItens
        .filter(item => {
          return item.checked;
        })
        .map(i => i.key)
        .join(',');

      setCheckedItems(its);

      setItens(newItens);
    },
    [itens],
  );
  return (
    <Container className="my-4">
      {loading && <Loading />}

      <>
        <h5>Gerar agendamento</h5>
        {optionsAgendas && (
          <Form ref={formRef} initialData={initialData} onSubmit={handleSubmit}>
            {exibePermissaoAdmin && (
              <PermissaoAdmin className="shadow border">
                <div>
                  <h6>Permissão necessária</h6>
                  <p>{mensagemPermissaoAdmin}</p>
                  <Input name="login" label="Código ou Email" />
                  <Input
                    name="senha"
                    label="Senha"
                    autoComplete="off"
                    type="password"
                  />
                  <Button
                    type="submit"
                    variant="primary mt-2"
                    size="sm"
                    disabled={loading}
                  >
                    {loading ? 'Enviando...' : 'Confirmar'}
                  </Button>

                  <Button
                    type="button"
                    variant="outline-secondary mt-2 float-right"
                    size="sm"
                    disabled={loading}
                    onClick={() => {
                      setExibePermissaoAdmin(false);
                      setMensagemPermissaoAdmin('');
                    }}
                  >
                    Voltar
                  </Button>
                </div>
              </PermissaoAdmin>
            )}
            <Row className="mb-2">
              <Col md={5} sm={6}>
                <SelectSync
                  name="AgeConfCodigo"
                  label="Agenda"
                  placeholder="Selecione a agenda"
                  isClearable={false}
                  handleSelectChange={handleOnChangeVazio}
                  options={optionsAgendas}
                />
              </Col>
              <Col md={3} sm={6}>
                <Input name="AgeData" label="Data" type="date" />
              </Col>
              <Col md={2} sm={6}>
                <SelectSync
                  name="AgeHora"
                  label="Hora"
                  placeholder="Selecione"
                  isClearable={false}
                  handleSelectChange={handleOnChangeVazio}
                  options={optionsHora}
                />
              </Col>
              <Col md={2} sm={6}>
                <br />
                <Button
                  type="submit"
                  size="sm"
                  className="mt-1"
                  disabled={loading || checkedItems === ''}
                >
                  Gerar
                </Button>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={12}>
                <input
                  className="d-none"
                  value={checkedItems}
                  onChange={() => console.log(itens)}
                  ref={checkRef}
                />

                {itens.map(proc => (
                  <ContainerCheck
                    key={`ch-${proc.name}${Math.random() * 999999999999}`}
                  >
                    <Checkbox
                      name={proc.name}
                      checked={proc.checked}
                      onChange={handleChange}
                    />
                    <label htmlFor={proc.name}>{proc.label}</label>
                  </ContainerCheck>
                ))}
              </Col>
            </Row>
          </Form>
        )}
      </>
    </Container>
  );
};

export default CheckupAgendamento;
